import CustomLink from './components/CustomLink';
import React from 'react';

export const translations = {
  en: {
    navbar: {
      about: "About Me",
      articles: "My Articles",
      experience: "Professional Experience",
      languageName: {
        en: "English",
        nl: "Dutch"
      },
      subscribe: "Subscribe",
      aria: {
        darkMode: "Toggle dark mode",
        language: "Choose language",
        home: "Home",
        github: "GitHub",
        linkedin: "LinkedIn",
      },
    },
    footer: {
      copyright: "Pieter Floris Jacobs. All rights reserved.",
      location: "Amsterdam, the Netherlands"
    },
    notFound: {
      title: "Page not found",
      text: "This page doesn't exist, or it has moved.",
      home: "Back to the homepage",
    },
    seo: {
      home: {
        title: "Pieter Floris Jacobs",
        description: "Pieter Floris Jacobs: AI engineer building for start- and scale-ups with Jacobian Systems, bringing young talent to them with STARTUP.CAREER, and writing about the European tech ecosystem.",
      },
      articles: {
        title: "Articles — Pieter Floris Jacobs",
        description: "Articles and research by Pieter Floris Jacobs on artificial intelligence, from world models and deepfakes to AI and jobs, and the European tech ecosystem.",
      },
      experience: {
        title: "Experience — Pieter Floris Jacobs",
        description: "The career timeline of Pieter Floris Jacobs: founder of Jacobian Systems and STARTUP.CAREER, product and AI engineering at OneThird, and an MSc in Artificial Intelligence.",
      },
    },
    homepage: {
      frontpage: {
        title: "Hi I'm Pieter",
        intro: (<>With a background in AI, both academic and practical, I like to work at the forefront of innovation: building for start- and scale-ups through Jacobian Systems, bringing young people to them with <CustomLink to="https://startup.career" external={true}>STARTUP.CAREER</CustomLink>, and writing about the European tech ecosystem in my <CustomLink to="/articles">articles</CustomLink>. On this website you can explore all of this, and get to know me a bit better.</>),
        explore: "See What I'm Building"
      },
      about: {
        title: "About Me",
        summary: "I like to use my creativity to help solve the (many) large-scale problems we are facing today. Many of these can be both depressing and frustrating to me. However, I don't enjoy sitting by and waiting for things to get worse. Instead, I like to combine my technical expertise in AI with an entrepreneurial mindset to work on solutions that make a visible impact. Making progress on this, albeit sometimes in small steps, drives me. Quite a nice feedback loop, if I do say so myself.",
        learnMore: (<>Learn more about me by clicking through the topics below or by going over my <CustomLink to="/experience">experience</CustomLink>.</>),
        items: {
          ai: {
            "title": "AI Specialist",
            "content": (
              <div className='flex flex-col gap-y-2'>
                <p>
                  I've spent a ridiculous amount of time on chess and various trading card games over the years and am a proud game enthusiast.
                  Strategizing paired with sometimes tough decision-making gets a large smile on my face (when I win).
                  This love for games eventually introduced me to Artificial Intelligence, as various algorithms were being created in this field that aimed to automate decision making in strategy games. 
                  I was fascinated by how quickly computers learned to master these games, to the point where even human grandmasters became easy opponents. Computer chess has a history which is very fun to read about even if you don't have a technical background. Naturally, it raised many questions for me:
                </p>
                <ul className='ml-4 list-disc list-inside'>
                  <li>How do these algorithms work?</li>
                  <li>What other complex tasks could AI handle?</li>
                  <li>Are these models truly intelligent, and can human intelligence be replicated?</li>
                </ul>
                <p>
                  Seeking answers, I started studying in AI at the <CustomLink to="https://www.rug.nl/" external>University of Groningen</CustomLink>.
                </p>
                <p>
                  Many of my questions were answered during my BSc. and MSc. in AI, although the question on human intelligence remains great material for discussion.
                  I learned about the fundamental principles of AI and the building blocks behind it. The various fields within AI were covered, from robotics to multi-agent systems to the now well-known machine and deep learning (because yes, these are not the same as AI but are mere parts of it).
                  It was a lot of new information, most of which I found fascinating, especially when I could apply it practically. A lot of the now established models and algorithms were still in its early stages back then, and it's interesting to look back and reminisce about how few people really understood what my field was all about.
                </p>
                <p>
                  Nowadays, things are quite different. AI has become a buzzword, and everyone seems eager to jump on the hype. While many now think Large Language Models are equivalent to AI, people at least have some semblance of what I do. Working with AI has also become far more accessible.
                  Any beginner programmer can, with just a few lines of code, build a neural network or some other complex model and quickly achieve acceptable results. As time goes on, this will only get easier, especially with quick advancements we see in LLMs.
                </p>
                <b>So then what is my added value?</b>
                <p>
                  I believe it lies in having a deep understanding of what's going on behind the scene and knowing the backgrounds and purposes of the different components of what we call AI. To innovate in the field and properly apply AI, I believe it's crucial to start from first principles.
                  This also includes understanding how the brain works and which aspects of psychology and philosophy are relevant when simulating human intelligence. These are key to answering some of the big questions we still face today.
                </p>
                <p>
                  Just to name a few:
                </p>
                <ul className='ml-4 list-disc list-inside'>
                  <li>What role does consciousness play in AI, or god forbid, what even is consciousness?</li>
                  <li>How can we develop biologically plausible AI?</li>
                  <li>How do humans create and learn language?</li>
                  <li>How can we ensure AI safety?</li>
                </ul>
                <p>
                  My goal is to regularly write about these kinds of questions and share my thoughts with you through online content. Not only because I enjoy sharing my knowledge, but also because it helps keep me sharp!
                </p>
              </div>
            ),
          },
          "focus": {
            "title": "Focus on Impact",
            "content": (

              <div className='flex flex-col gap-y-2'>
                <p>
                  The projects I'm interested in tend to be quite ambitious. I am motivated by ideas that
                  have the potential to have a significant positive influence on the world. Whereas I used to believe that people are quite insignificant, and won't be able
                  to change the trajectory of the bigger picture, I have become a bit more of an optimist in recent times.
                </p>
                <p>
                  <b>Because why not try?</b>
                  <br></br>There are enough examples of small groups of people going from zero to one.
                  I believe that I am bright and that I have some talent, so why wouldn't I be able to do the same? It would be a real shame to waste my time and talent on anything less,
                  especially now that I have the energy and mindset to take on large scale challenges. When I find a project that resonates with me, I give it everything I got.
                </p>
              </div>
            ),
          },
          "entrepreneurial": {
            "title": "Entrepreneurial Mindset",
            "content":
              (
                <div className='flex flex-col gap-y-2'>
                   <p>
                    To me, entrepreneurship is the freedom to create and build whatever I want, in whatever way I think is best. This partly comes from me being stubborn, and  also from the fact that I like to have strong opinions on pretty much everything.
                    But it's also just because of the fact that it is exciting; immediately acting on opportunities and not being too risk averse gets you into the most interesting situations.
                    Moreover, I want to be responsible for a success or failure of something I'm working on, because this is the way I deliver and learn best.
                    From founding my own company to driving innovation at <CustomLink to='https://onethird.io' external>OneThird</CustomLink> and <CustomLink to='https://youngadvisorygroup.nl' external>YAG</CustomLink>, 
                    I plan to always continue working in entrepreneurial environments, it is a large part of who I am.             
                  </p>
                  <p>
                    This is probably best exemplified by some fun stories from my childhood. I think most children are fully true to themselves and their actions reveal much more about their character and personality than adults' do.
                    No, I don't want to start a nature versus nurture debate. I am merely trying to be able to use my younger self as an example of my entrepreneurial mindset. From a young age, I was always
                    busy with ideating, making things, and selling them.
                  </p>

                  <p>
                    To exemplify, back in primary school I created strategy games that the entire school could participate in.
                    I designed detailed maps of fantasy worlds where students could buy their own land. They could furnish this land with various products that I offered, which I
                    would then neatly draw on their section of the map. <i>But nothing in life comes for free!</i> I decided we needed a currency so that people could pay me and trade with each other;
                    scrap paper became the improvised money. Before long, the economy was booming, with kids trading, building, and, inevitably, waging wars against each other.
                    War, of course, is expensive, so I quickly became a fairly wealthy individual, meaning my mother never had to worry about running out of paper for grocery lists again.
                  </p>

                  <p>
                    This success, along with other side hustles like my cleaning gum trade and my shop of knife-like objects made from ballpoint pens, naturally motivated me to turn more of my ideas into reality.
                    In high school, I flipped various popular sneakers to make some money on the side and even attempted to create an MMORPG to play with my friends. Fun project, but didn't quite work out the way I envisioned it. Moreover, I decided to write a Black Mirror-esque fiction book about a monopolist trading in data (which, in hindsight,
                    turned out to be a rather accurate prediction of today's world). My co-author and I published the book and sold it to anyone who was curious.
                    Seeing people enjoy or benefit from the things I create has always been incredibly rewarding, and that drive has stayed with me throughout the years.
                  </p>
                 
                </div>

              ),
          },
          "creative": {
            "title": "Creative and Fixated",
            "content":
              (
                <div className='flex flex-col gap-y-2'>
                  <p>
                    If you can clearly visualize something, I think it likely that you can also make it happen. It's a shame Conor McGregor has lost his damn mind, because he had quite <CustomLink to="https://www.youtube.com/shorts/8CW3lUU_s7Q" external>a nice quote</CustomLink> about this.
                    The similar 'build, build, build' mantra the tech world preaches is kind of one dimensional in my opinion, but is also a good example of what I like to do.
                    I've been making music from a young age, enjoy writing short fiction, started a humorous podcast with a good friend,
                    and love creating customized games to play with my friends.
                    Perhaps that's also why I like programming, because, like the aforementioned, it is a means to an end for me to build the stuff I think of. 
                  </p>
                  <p>
                    After starting with web development, I realized that technology was a great way to express my creativity. When I had centered enough div's, I thought it time to explore other topics,
                    such as quantum computing, AI, and the (controversial) world of crypto. I started building, and for instance invested a lot of time in building decentralized apps with Solidity.
                    Even though it turns out that decentralization has limited use cases, I loved the process of learning a new technology.
                  </p>
                  <p>  
                    When I find a topic that resonates with me, like the decentralized internet, I tend to lose myself in it. I'm highly competitive and love challenging myself to become the best at something,
                    and I think this obsessive focus I have comes from that. You can clearly see it throughout my <CustomLink to="/experience" external={false}>professional career</CustomLink> but it has also
                    shown in other endeavors. For example, when I started kickboxing, it had to be five times a week, and it was important for me to work towards an actual fight in the ring.
                    Martial arts quickly became a new passion. Maybe an even more convincing argument, which gamers can relate to would be the fact that the Soulsborne series is my favorite.
                    </p>
                </div>

              )
          }
        }
      },
      currentWork: {
        heading: "Currently Building",
        intro: "After two years as a Data Scientist/ML Engineer, then product manager, at OneThird, I left in August 2026 to build my own things full-time. Below you find the ventures I'm currently focused on.",
        sideProjectsLabel: "Also tinkering with: Rewind",
        website: "Website",
      },
      contact: {
        title: "Get in Touch",
        description: (<>Got an exciting idea for a collaboration? <br />
          Questions about the projects I work on? <br />
          Or maybe you just want to chat about the latest tech trends? <br /></>),
        button: "Contact Me"
      }
    },
    articlePage: {
      articles: {
        category: {
          "science": "Scientific",
          "medium": "Medium",
        },
        alLabelling: {
          title: "Active learning for reducing labeling effort in text classification tasks",
          excerpt: `Labelling data manually is very time consuming and costly.
      In this study, we explore how to reduce this labelling effort through use of active learning.
      We compare different uncertainty-based algorithms to select a subset of data on which, in hopes of achieving
      similar performance to using the full dataset, to train BERTbase on in a text classification setting.
      Moreover we propose and examine heuristics to address scalability and outlier selection issues linked to pool-based active learning. 
      While these heuristics didn’t improve AL, 
      our findings show that uncertainty-based AL outperforms random sampling, 
      though the performance gap narrows as query-pool sizes increase.`,
        },
        LLMDSL: {
          title: "Developing Large Language Models for Quantum Chemistry Simulation Input Generation",
          excerpt: `Scientists often struggle with Domain-Specific Languages (DSLs) in fields like computational chemistry. 
      In this paper, we propose a general framework for using Large Language Models (LLMs) to generate DSL code, using
      the ORCA quantum chemistry package as a case study. 
      This framework includes various prompt engineering techniques, finetuning with different synthetic datasets, and use of retrieval augmented generation. 
      Our framework significantly boosted LLM performance, even surpassing the GPT-4o model when adopting the less powerful GPT 3.5 model as base.`,
        },
      },
      title: "Articles",
      filters: {
        scientific: "Scientific",
        opinion: "Opinion",
        informative: "Informative",
      },
      searchLabel: "Search articles",
      searchPlaceholder: "Search articles…",
      sort: {
        newest: "Newest first",
        oldest: "Oldest first",
        shortest: "Shortest read",
        longest: "Longest read",
      },
      timeRead: "min read"
    },
    projectsPage: {
      projects: {
        startupCareer: {
          title: "STARTUP.CAREER",
          description: "We inspire and enable young people to help solve the biggest problems of our time. We believe startups are the best places to do that and therefore curate Europe’s most ambitious to tell their story to people at the start of their careers. We make their missions, people, and opportunities visible, giving talent the context and access needed to act. Today, we create in-depth company profiles consisting of a short-form documentary accompanied by a long-form article. Ultimately, we’re building the platform people turn to when they want to make an impact with their careers.",
          period: "September 2026 – Present",
        },
        jacobianSystems: {
          title: "Jacobian Systems",
          description: "I combine an extensive technical background in AI with product experience to build and develop highly technical, AI-driven products for start- and scale-ups, from scratch to production, or to assess what they already have and what can be improved.",
          currently: "Currently working on ML models for hyperspectral imaging, and a computer vision product that automatically inspects fresh produce. It replaces manual visual inspection and gives distributors and growers quality insights.",
        },
        rewind: {
          title: "Rewind",
          description: "Shared event photo vaults with a deliberate delay. Guests snap photos all night, but the pictures only develop later. The magic is in the wait.",
          period: "2025 – Present",
        },
      },
    },
    experiencePage: {
      title: "Explore my Career",
      downloadCv: "or download my CV",
      downloadCvAlone: "download my CV",
      types: {
        work: 'Work',
        education: 'Education',
        other: 'Achievements',
        all: "All"
      },
      tabs: {
        timeline: 'Career Timeline',
        // Renamed from "Side Projects" — tentative, per Pieter ("for now").
        sideprojects: 'Hobby Projects',
      },
      expand: "Expand All",
      collapse: "Collapse All",
      showMore: "Show More",
      showLess: "Show Less",
      experiences: {
        bachelor: {
          title: "BSc. Artificial Intelligence",
          
          date: "September 2018 - July 2021",
          where: 'University of Groningen',
          description: (
            <>
              Graduated with an average of 7.7, my course list can be found <CustomLink to="/documents/bachelor_courses.pdf" download="bachelor_courses.pdf">here</CustomLink>.
              I went on to publish an adjusted version of my bachelor thesis in the BNAIC 2021 conference proceedings, the final article can be read <CustomLink to="https://arxiv.org/abs/2109.04847" external={true}>here</CustomLink>.
            </>
          ),
        },
        master: {
          title: "MSc. Artificial Intelligence",
          date: "September 2021 - July 2024",
          where: 'University of Groningen',
          description: (
            <>
              Graduated cum laude with an average of 8.6, taking the Computational Intelligence track. This specialization has a focus on machine learning, my full course list can be found <CustomLink to="/documents/master_courses.pdf" download="master_courses.pdf">here</CustomLink>.
              Moreover, I published an adjusted version of my master thesis in the <CustomLink to="https://pubs.rsc.org/en/journals/journalissues/dd#!recentarticles&adv" external={true}>Digital Discovery journal</CustomLink> (impact factor: 6.2).
              The article can be found <CustomLink to="https://chemrxiv.org/engage/chemrxiv/article-details/66d20058a4e53c487628f63b" external={true}>here</CustomLink>.
            </>
          )
        },
        yag: {
          date: "May 2022 - February 2024",
          title: 'Strategy Consultant',
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>Coached by top consulting firms, like Berenschot, McKinsey, Roland Berger and BCG, I provided strategic advice to small and medium
        enterprises, multinationals, and government institutions. Example projects I worked on include a life cycle analysis of paper and digital books as well as advising about the use of AI in the Dutch Educational system.
            </p>
            <p>
        Among other things, I gained experience in how to brainstorm and initiate new ideas in a larger organization, in presenting and pitching, in how to lead a team and in project management.
        </p>
        </div>)
        },
        omun: {
          title: "Fullstack Website Developer (Founder & Director)",
          date: "August 2020 - January 2024",
          description: 
          <div className='flex flex-col gap-y-2'>
            <p>
            Designed, built and deployed custom web applications for clients, mainly through use of the MERN stack. One project example involved developing a platform where a major Malaysian fish seller could record all its purchases and sales.
            </p>
            <p>
            I learned how to tackle the challenges that come with implementing software and AI for real user products, where data is often limited and where there is more to deliver than just good software to get to a result that a client is happy with.
            </p>
            </div>
        },
        kickbox: {
          title: "Kickboxing Fight",
          description: `Trained six times a week in preparation for a kickboxing event where I entered the ring for a match which I ended up winning. I was trained by a former world champion and the training camp spanned a total of three months.
          
          This was a challenging period, where I gained a lot of discipline, confidence and had to learn how to persevere even when things looked grim every now and then.`,
        },
        umcg: {
          date: "March 2022 - March 2023",
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
            Worked on a computer vision project concerning the detection of Aspergillus in the lungs. We used machine learning to discriminate
            proven fungal infections from control.
            </p>
            <p>
            My main takeaway from this experience was that I enjoy working in a multidisciplinary environment. I had to learn to quickly understand the expert knowledge of others to a degree where I could translate it into the product I was building for them, and of course in explaining my expert knowledge to others in a simple to understand manner.
            </p>
          </div>
          ),
          where: 'University Medical Centre Groningen',
        },
        bg: {
          date: "September 2020 - April 2024",
          title: "Captain and Tour Guide",
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
              Toured people around the canals of Groningen by boat in which I served as the captain. This included doing historical tours where I told about the history and notable parts of the city.
            </p>
            <p>
            I gained a lot of experience in presenting to different types of people, learned how to get to have fun conversations with all types of personalities and learned to get and keep authority over a group of people you don't know.
            </p>
          </div>
          ),
        },
        doordeweeks: {
          title: "Podcast Co-host",
          description: (
            <>
              Ran a monthly humorous podcast with comedian and good friend <CustomLink to="https://maxvanschaik.nl/" external={true}>Max van Schaik</CustomLink>. This was a fun project where we went out of our comfort zone
              to do all sorts of ridiculous challenges. The episodes can be found on <CustomLink to="https://open.spotify.com/show/6QuNwZVcODW9uakJzpOLs7?si=5c36bacf54af4e77" external={true}>Spotify</CustomLink>. It taught me about the recording and editing of speech, and about structuring the content for online media.
            </>
          ),
        },
        minor: {
          title: 'Minor Entrepreneurship',
          where: 'University of Groningen',
          description: `Aside from taking courses on scientific and proven approaches to entrepreneurship as well as the legal and financial parts of it, 
      I worked in a multidisciplinary project group to develop an empirically grounded solution for a practical business development challenge. 
      Me and my group members developed a product to monitor what privacy policies of used products conflict with the user's privacy preferences. We won
      the award for best product pitch.`,
        },
        dialogic: {
          date: "February 2021 - July 2021",
          title: "Machine Learning Engineer (Intern)",
          description: (
            <div className='flex flex-col gap-y-2'>
              <p>
              Created an application to reduce labeling effort of text classification tasks through use of BERT language model with active learning. The application had to be usable and understandable for employees with no knowledge of machine learning.
              </p>
              <p>
              I gained a lot of understanding on how to build a product for people that don't have much technical experience and how to integrate a new product into a company's day-to-day operations.
              </p>
          </div>
          ),
        },
        jacobianSystems: {
          date: "August 2026 - Present",
          where: 'Jacobian Systems',
          title: 'Founder',
          description: "I combine an extensive technical background in AI with product experience to build and develop highly technical, AI-driven products for start- and scale-ups, from scratch to production, and to assess existing systems and what can be improved.",
        },
        startupCareer: {
          date: "September 2026 - Present",
          where: 'STARTUP.CAREER',
          title: 'Co-Founder',
          description: "Building a community platform for the European startup ecosystem that inspires and helps young people to build their careers at the most ambitious startups, through professional film, editorial articles, events and a platform with a growing base of sign-ups.",
        },
        onethird: {
          date: "October 2024 - August 2026",
          where: 'OneThird',
          title: 'Data Scientist & ML Engineer',
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
              OneThird predicts the shelf life and quality of fresh produce without damaging it. For strawberries, our largest category, I led the technical roadmap and built the data science and machine learning behind it:
            </p>
            <ul className='list-disc list-inside'>
              <li>Automated shelf-life and Brix prediction from NIR spectra, metadata and images: from manually run algorithms to continuously retrained deep learning pipelines (custom 1D CNNs, transformers) that perform well for every customer</li>
              <li>Made noisy, subjective shelf-life labels more objective across customers, while keeping them aligned with each customer's own judgement</li>
              <li>Built the computer vision system from an early prototype to fine-grained models trained on 1M+ images, assessing size, colour and defects (white shoulders, wet spots, mold) to each customer's spec; a quality check now takes ~5 seconds instead of minutes</li>
              <li>Replaced Power BI with our own analytics stack for customer dashboards (Postgres, Cube.js, React)</li>
            </ul>
            <p>
              After almost two years I left to build my own things full-time. I still work with OneThird on projects through Jacobian Systems.
            </p>
          </div>
          ),
        },
        onethirdPm: {
          date: "November 2025 - August 2026",
          where: 'OneThird',
          title: 'Product Manager',
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
              On top of my engineering work, I owned our computer vision product, reporting to the founder:
            </p>
            <ul className='list-disc list-inside'>
              <li>Owned the roadmap, go-to-market and business model, steering the developers building it</li>
              <li>Scaled it to ~20 large accounts at top-5 distributors in six European countries, turning customer needs into product decisions</li>
              <li>Built a repeatable playbook to onboard customers, keep their models current, and help them cut manual testing and waste</li>
            </ul>
          </div>
          ),
        },
          hardlist: {
        date: "September 2025 - February 2026",
        title: "Volunteer",
        where: 'Hardlist',
        description: (
          <div className='flex flex-col gap-y-2'>
            <p>
              Helping connect and showcase the European hard tech ecosystem through:
            </p>
            <ul className='list-disc list-inside'>
              <li>Supporting the organization of events that bring together founders, investors, engineers, and enthusiasts.</li>
              <li>Researching startups for inclusion on the <CustomLink to='https://hardli.st' external>Hardli.st</CustomLink> website.</li>
            </ul>
          </div>
        ),

        },
        rewind: {
          date: "2025 - Present",
          title: "Founder",
          where: 'Rewind',
          description: (
            <>
              A side project: shared event photo vaults with a deliberate delay — guests snap photos all night, but the pictures only develop later. Built and run in my spare time alongside <CustomLink to="https://startup.career" external={true}>STARTUP.CAREER</CustomLink>.
            </>
          ),
        },

      }
    },
  },
  nl: {
    navbar: {
      about: "Over mij",
      articles: "Mijn artikelen",
      experience: "Werkervaring",
      languageName: {
        en: "Engels",
        nl: "Nederlands"
      },
      subscribe: "Abonneer je",
      aria: {
        darkMode: "Donkere modus aan/uit",
        language: "Kies taal",
        home: "Home",
        github: "GitHub",
        linkedin: "LinkedIn",
      },
    },
    homepage: {
      frontpage: {
        title: "Hoi, ik ben Pieter",
        intro: (<>Mijn achtergrond ligt in AI, zowel in mijn opleiding als in de praktijk, en ik ben graag bezig met nieuwe technologie. Met Jacobian Systems ontwikkel ik voor ambitieuze start- en scale-ups producten met een focus op AI. Met <CustomLink to="https://startup.career" external={true}>STARTUP.CAREER</CustomLink> inspireer en help ik jonge mensen om daar te gaan werken, en in mijn <CustomLink to="/articles">artikelen</CustomLink> schrijf ik over het Europese tech-ecosysteem. Op deze website vind je het allemaal, en leer je mij wat beter kennen.</>),
        explore: "Lees meer"
      },
      about: {
        title: "Over Mij",
        summary: "Ik werk graag aan het oplossen van de grootschalige problemen waarmee we tegenwoordig worden geconfronteerd. Werk genoeg dus. Veel van deze problemen, sommige existentieel, andere meer praktisch, vind ik zowel deprimerend als frustrerend. Zoals iedere andere Nederlander klaag ik maar al te graag over hoe alles naar de klote gaat. Echter is stil zitten en wachten tot er iets verandert niet mijn soort oplossing. In plaats daarvan combineer ik graag mijn technische expertise in AI met een ondernemende mindset om te werken aan oplossingen die een zichtbaar verschil maken. Vooruitgang hierin boeken geeft me mijn drive. Best een mooie feedbackloop, al zeg ik het zelf.",
        learnMore: (<>Als je meer wilt leren over hoe dit tot uiting komt of hoe ik verder in elkaar steek, kun je op de onderstaande onderwerpen klikken of door <CustomLink to="/experience">mijn professionele ervaring</CustomLink> heen gaan.</>),
        items: {
          ai: {
            "title": "Kunstmatige Intelligentie",
            "content": (
              <div className='flex flex-col gap-y-2'>
                <p>Vanaf jongs af aan ben ik altijd een spelletjesmens geweest. Ik schaak(te) erg veel en heb ook door de tijd heen
                  verschillende trading card games gespeeld. Ik vond het leuk om bij dit soort spellen beter te worden in het maken van de juiste keuzes en om te leren over alle mogelijke strategieën.
                  Mijn liefde voor spelletjes leidde ertoe dat ik in aanraking kwam met Kunstmatige Intelligentie, wat werd gebruikt om computers deze spellen op hoog niveau te kunnen laten spelen.
                  Ik vond (en vind) dit fascinerend, en las bijvoorbeeld graag over de uitgebreide geschiedenis van computerschaken. Dit riep een aantal vragen bij mij op:
                </p>
                <ul className='ml-4 list-disc list-inside'>
                  <li>Hoe werken deze algoritmes?</li>
                  <li>Wat voor andere complexe taken zou KI kunnen doen? </li>
                  <li>Zijn de modellen echt intelligent, en kan menselijke intelligentie worden nagebootst?</li>
                </ul>
                <p>
                  Op zoek naar antwoorden begon ik Kunstmatige Intelligentie te studeren aan de <CustomLink to="https://www.rug.nl/" external>Rijksuniversiteit Groningen</CustomLink>.
                </p>
                <p>
                  Gelukkig kreeg ik deze tijdens mijn Bsc. en Msc. KI, al blijft de kwestie over menselijke intelligentie nog altijd mooi voer voor discussies. Ik leerde over de basisprincipes van KI, en uit welke bouwstenen
                  alles is opgebouwd. De verschillende velden binnen KI kwamen natuurlijk allemaal aan bod, van robotics tot multi-agent systems tot de inmiddels welbekende machine en deep learning (want ja, deze zijn niet hetzelfde als KI maar slechts een onderdeel ervan).
                  Heel veel informatie waarvan ik het meeste razend interessant vond, vooral wanneer het concreet toepasbaar was. Veel van de moderne modellen en algoritmes stonden toentertijd nog in de kinderschoenen en het is nu leuk om terug te kijken
                  op hoe mensen geen idee hadden van wat mijn studie inhield.
                </p>
                <p>
                  Dit is natuurlijk tegenwoordig wel anders. AI is inmiddels gewoon een buzzword, en allerlei snelle jongens en dames springen maar al te graag op de hype.
                  Dat KI voor de meeste nu eigenlijk gelijk te staan aan Large Language Models (LLMs), doet niet af van dat
                  iedereen inmiddels bekend is met de term. Daarnaast is het ook een stuk laagdrempeliger geworden om met en aan KI te werken.
                  Elke beginnende programmeur kan
                  in enkele regels code een neuraal netwerk of een ander ingewikkeld model opzetten en deze gebruiken om redelijke resultaten te behalen.
                  Dit zal alleen maar makkelijker worden, zeker met de hulp van die eerder genoemde LLMs.
                  </p> 
                  <b>Dus wat is mijn toegevoegde waarde dan nog?</b>
                  <p>
                  Ik denk dat dit neer komt op de kennis van de daadwerkelijke werking van wat er achter de schermen gebeurt, en van wat de achtergronden en doelen
                  van de verschillende delen van wat we KI noemen zijn. Om KI goed te kunnen inzetten en om te kunnen innoveren binnen het veld,
                  geloof ik dat men moet beginnen bij de eerste principes. Dit omvat ook kennis over hoe de hersenen werken
                  en welke delen van de psychologie en filosofie relevant zijn voor het simuleren van menselijke intelligentie. Deze kennis is nodig bij verschillende grote vragen
                  die in de nabije toekomst beantwoord moeten worden.
                  </p>
                  <p>
                  Om er maar een paar te noemen:
                  </p>
                  <ul className='ml-4 list-disc list-inside'>
                    <li>Welke rol speelt bewustzijn in KI, of god verhoede, wat is bewustzijn eigenlijk?</li>
                    <li>Hoe komen we tot biologisch plausibele KI?</li>
                    <li>Hoe creëren en leren mensen taal?</li>
                    <li>Hoe kunnen we veilige KI maken?</li>
                  </ul>
                <p>
                Mijn doel is om regelmatig over dit soort vragen te schrijven en mijn gedachten te delen via online content. Mede omdat ik het leuk vind om
                mijn kennis te delen maar ook omdat dit mij zelf scherp houdt!
                </p>
              </div>
            ),
          },
          "focus": {
            "title": "Impact Maken",
            "content": (
              <div className='flex flex-col gap-y-2'>
                <p>
                  Ik werk over het algemeen aan vrij ambitieuze projecten. Er moet een groot probleem liggen
                  om mijn interesse te trekken, eentje waarover ik twijfel of het eigenlijk wel op te lossen valt. Dan kan ik namelijk
                  aan mezelf bewijzen dat dit wel degelijk mogelijk is. 
                  Ik heb de neiging om, wanneer ik rationeel nadenk, al snel in een nihilistische manier van denken te vervallen en te gaan sombermansen. 
                  Mijn manier van werken is hier een tegenreactie op denk ik, om het idee dat mijn bestaan niet voor niets is, en dat kleine groepjes individuen wel degelijk een verschil kunnen maken te kunnen bevestigen.
                  Dat houdt mij positief en gemotiveerd.
                </p>
                <p>
                  Die manier van denken is ook zo gek nog niet. Er zijn namelijk tal voorbeelden van mensen die dingen neerzetten die niemand ooit voor mogelijk hield; totdat het werkelijkheid werd. Ik denk dat ik wel het een en ander in mijn mars heb,
                  dus waarom zou ik dat dan niet ook kunnen? Het zou zonde zijn als ik mijn tijd en talent zou verspillen aan minder, nu ik nog de energie en mindset heb om volle bak aan dit soort dingen te werken.
                </p>
              </div>
            ),
          },
          "entrepreneurial": {
            "title": "Ondernemen",
            "content":
              (
                <div className='flex flex-col gap-y-2'>
                  <p>
                    Ik ben eigenwijs, oplossingsgericht, en heb over de meeste onderwerpen (graag) een sterke mening.
                    Ondernemerschap is voor mij een mooie manier om dit te uiten. Ik heb graag de vrijheid om te kunnen maken wat ik wil, 
                    op de manier die ik het beste acht. Daarnaast valt de lol en spanning van ondernemen ook gewoon niet te ontkennen: 
                    door niet risicoavers te zijn en hands on in te gaan op kansen kom je in de mooiste situaties.
                    Van het oprichten van mijn eigen bedrijf tot het stimuleren van innovatie bij <CustomLink to='https://onethird.io' external>OneThird</CustomLink> en <CustomLink to='https://youngadvisorygroup.nl' external>YAG</CustomLink>,
                    ik ben van plan altijd in ondernemende omgevingen te blijven werken.  Die ondernemende kant heb ik eigenlijk altijd al gehad. 
                  </p>
                  <p>
                    Kinderen zijn helemaal zichzelf en daarom valt er geloof ik al veel te zeggen over het karakter en de persoonlijkheid
                    van wie ze als mens zijn en gaan worden. Nee, ik wil hier geen nature versus nurture debate neer gaan kalken, maar even
                    mijn jongere zelf erbij halen als voorbeeld van mijn ondernemende mindset. Van jongs af aan was ik namelijk al druk met nieuwe
                    dingen bedenken, maken, en het verkopen hiervan vond ik ook erg belangrijk. 
                  </p>
                  <p>
                    Een mooi voorbeeld gaat terug naar de basisschool, waar ik uitgebreide kaarten van fantasiewerelden ontwierp waar leerlingen hun eigen land konden kopen. Dit land konden ze inrichten met
                    allerlei producten die ik aanbood en voor hun heel mooi op hun stukje van de kaart erbij tekende.
                    <i>Maar voor niets gaat de zon op!</i> Ik besloot dat we een munteenheid nodig hadden zodat men mij kon betalen, en met elkaar kon handelen; oud papier werd het geïmproviseerde geld.
                    Al snel bloeide de "economie", met kinderen die handelden, bouwden en, onvermijdelijk, oorlogen tegen elkaar voerden.
                    Oorlog is natuurlijk duur, dus ik werd al snel een behoorlijk rijk individu, wat betekende dat mijn moeder zich nooit meer zorgen hoefde te maken dat het papier voor boodschappenlijstjes op zou raken.
                  </p>
                  <p>
                    Dit soort succes, naast mijn kneedgom handel en de verkoop van uit balpen gemaakte mesjes, motiveerde mij om mijn ideeën uit te blijven werken.
                    In plaats van een regulier PWS, besloot ik om een Black Mirror-esque boek te schrijven over een monopolist die handelde in data (iets wat, achteraf gezien, een behoorlijk nauwkeurige voorspelling van de wereld van vandaag bleek te zijn).
                    Deze bracht ik met mijn co-auteur uit en verkochten we aan iedereen die nieuwsgierig was.
                    Ik veranderde mijn passie voor sneakers ook in een kleine handel en probeerde zelfs, hoewel zonder veel succes, een MMORPG te maken om met mijn vrienden te spelen.
                    Het zien van mensen die genieten van of profijt hebben van de dingen die ik maak, is altijd enorm bevredigend geweest, en die drang is me door de jaren heen bijgebleven.
                  </p>
                  
                </div>
              ),
          },
          "creative": {
            "title": "Creatief en Obsessief",
            "content":
              (
                <div className='flex flex-col gap-y-2'>
                  <p>
                    Als je iets duidelijk kunt inbeelden, geloof ik dat je het ook werkelijkheid kunt maken. Jammer dat Conor McGregor tegenwoordig volledig is doorgesnoven, want hij had ooit <CustomLink to="https://www.youtube.com/shorts/8CW3lUU_s7Q" external>een best aardige quote</CustomLink> hierover.
                    De soortgelijke 'bouwen, bouwen, bouwen' mentaliteit waar de techwereld zo van houdt, vind ik persoonlijk wat eendimensionaal, maar is een ander goed voorbeeld van wat ik graag doe.
                    Ik maak al van jongs af aan muziek, schrijf graag korte fictieverhalen, begon samen met een goede vriend een humoristische podcast
                    en vind het leuk om op maat gemaakte spelletjes te ontwerpen om met mijn vrienden te spelen.
                  </p>
                  <p>
                    Toen ik begon met web development, besefte ik dat het een geweldige manier was om mijn creativiteit te uiten.  
                    Net als bij al het bovenstaande was het voor mij een middel om de ideeën in mijn hoofd tot leven te brengen.
                    Na het centreren van genoeg div's vond ik het tijd om andere onderwerpen te verkennen zoals quantum computing, AI en de (omstreden) wereld van crypto.
                    Ik begon te bouwen en stak bijvoorbeeld veel tijd in het ontwikkelen van gedecentraliseerde apps met Solidity.  
                    Hoewel decentralisatie in de praktijk helaas beperkte toepassingen blijkt te hebben, genoot ik enorm van het proces van leren door gewoon te gaan. Technologie
                    is voor mij de perfecte sector gebleken om dit voort te zetten.
                  </p>
                  <p>
                    Wanneer ik zo'n onderwerp vind dat met me resoneert kan ik mezelf er volledig in verliezen.  
                    Ik ben erg competitief en daag mezelf graag uit om ergens de beste in te worden, en ik denk dat die obsessieve focus daaruit voortkomt.  
                    Dat zie je duidelijk terug in mijn <CustomLink to="/experience" external={false}>professionele loopbaan</CustomLink>, maar ook in andere bezigheden.  
                    Toen ik bijvoorbeeld begon met kickboksen, moest dat meteen vijf keer per week, en ik vond het belangrijk om toe te werken naar een echt gevecht in de ring.  
                    Martial arts werd al snel een nieuwe passie.  
                    Een misschien nog beter voorbeeld van deze mentaliteit, die alleen gamers zullen begrijpen: Soulsborne is mijn favoriete spellenreeks.  
                  </p>

                </div>
              )
          }
        }
      },
      currentWork: {
        heading: "Waar ik nu mee bezig ben",
        intro: "Na twee jaar als data scientist/ML engineer en later productmanager bij OneThird ben ik in augustus 2026 voor mezelf begonnen. Hieronder zie je waar ik me nu op richt.",
        sideProjectsLabel: "Ook bezig met: Rewind",
        website: "Website",
      },
      contact: {
        title: "Neem Contact Op",
        description: (<>Wil je met mij samenwerken of heb je vragen over de projecten waar ik aan werk? <br />
          Of wil je gewoon met mij sparren over ontwikkelingen binnen tech, of over iets anders? <br />
          Klik op de knop beneden om met mij in contact te komen!</>),
        button: "Email Mij"
      },
    },
    articlePage: {
      articles: {
        category: {
          "science": "Wetenschappelijk",
          "medium": "Medium",
        },
        alLabelling: {
          title: "Active Learning om labellingsinspanning bij tekstclassificatietaken te verminderen",
          excerpt: `Het handmatig labelen van data is tijdrovend en kostbaar.
          In deze studie onderzoeken we hoe deze labellingsinspanning kan worden verminderd door gebruik te maken van Active Learning (AL).
          We vergelijken verschillende onzekerheidsgebaseerde algoritmen om een subset van de gebruikte dataset te selecteren waarop, in de hoop vergelijkbare prestaties te bereiken als bij het gebruik van de volledige dataset, BERTbase kan worden getraind in tekstclassificatie.
          Bovendien stellen we verschillende heuristieken voor en onderzoeken we deze om schaalbaarheids- en uitschieterselectieproblemen aan te pakken die verband houden met pool-gebaseerd AL.
          Hoewel deze heuristieken AL niet verbeterden,
          tonen onze bevindingen aan dat onzekerheidsgebaseerd AL beter presteert dan willekeurige sampling,
          hoewel het prestatieverschil kleiner wordt naarmate de grootte van de query-pool toeneemt.`,
        },
        LLMDSL: {
          title: "Ontwikkeling van Large Language Models voor het genereren van code voor simulaties in kwantumchemie",
          excerpt: `Wetenschappers worstelen vaak met domeinspecifieke programmeertalen (DSL's) in vakgebieden zoals computationele chemie.
          In dit artikel stellen we een algemeen framework voor om Large Language Models (LLM's) te gebruiken voor het genereren van DSL-code, waarbij het ORCA-kwantumchemiepakket als casestudy wordt gebruikt.
          Dit framework omvat verschillende technieken voor prompt-engineering, finetuning op verschillende synthetisch gecreëerde datasets, en gebruik van retrieval augmented generation.
          Ons framework verhoogde de prestaties van LLM's aanzienlijk en overtrof zelfs het GPT-4o-model bij gebruik van het minder krachtige GPT 3.5-model als basis.`,
        },
      },
      title: "Artikelen",
      filters: {
        scientific: "Wetenschappelijk",
        opinion: "Opinie",
        informative: "Informatief",
      },
      searchLabel: "Zoek artikelen",
      searchPlaceholder: "Zoek artikelen…",
      sort: {
        newest: "Nieuwste eerst",
        oldest: "Oudste eerst",
        shortest: "Kortste leestijd",
        longest: "Langste leestijd",
      },
      timeRead: "min leestijd"
    },
    projectsPage: {
      projects: {
        startupCareer: {
          title: "STARTUP.CAREER",
          description: "We willen dat meer jonge mensen meehelpen aan de grootste problemen van onze tijd, en volgens ons kan dat het best bij startups. Daarom kiezen we de meest ambitieuze startups van Europa uit en vertellen we hun verhaal aan mensen die net aan hun carrière beginnen: hun missie, hun mensen en hun kansen. Op dit moment doen we dat met bedrijfsprofielen, een korte documentaire met een uitgebreid artikel. Uiteindelijk moet het hét platform worden voor wie met zijn carrière impact wil maken.",
          period: "September 2026 – Heden",
        },
        jacobianSystems: {
          title: "Jacobian Systems",
          description: "Ik combineer een brede technische achtergrond in AI met ervaring in het klantgericht bouwen en uitrollen van producten. Daarmee ontwikkel ik technisch complexe producten met een focus op AI voor start- en scale-ups, van het eerste idee tot in productie, of kijk ik kritisch naar wat er al is en wat beter kan.",
          currently: "Op dit moment werk ik aan ML-modellen voor hyperspectrale beelden en aan een computervisieproduct dat vers fruit en groenten automatisch inspecteert. Het vervangt handmatige visuele controle en geeft distributeurs en telers inzicht in de kwaliteit.",
        },
        rewind: {
          title: "Rewind",
          description: "Een gedeeld fotoalbum voor je event, dat pas later tevoorschijn komt. Gasten maken de hele avond foto's en zien ze pas achteraf, net als vroeger met een wegwerpcamera.",
          period: "2025 – Heden",
        },
      },
    },
    experiencePage: {
      title: "Carrière Overzicht",
      downloadCv: "of download mijn cv",
      downloadCvAlone: "download mijn cv",
      types: {
        work: 'Werk',
        education: 'Onderwijs',
        other: 'Prestaties',
        all: 'Alles',
      },
      tabs: {
        timeline: 'Carrière Tijdlijn',
        sideprojects: 'Hobbyprojecten',
      },
      expand: "Klap Mijlpalen uit",
      collapse: "Klap Mijlpalen in",
      showMore: "Toon meer",
      showLess: "Toon minder",
      experiences: {
        bachelor: {
          title: "BSc. Kunstmatige Intelligentie",
          date: "September 2018 - Juli 2021",
          where: 'Rijksuniversiteit Groningen',
          description: (
            <>
              Afgestudeerd met een gemiddelde van 7,7. Mijn vakken met bijbehorend cijfer zijn <CustomLink to="/documents/bachelor_courses.pdf" download="bachelor_courses.pdf">hier</CustomLink> te vinden.
              Ik heb een aangepaste versie van mijn bachelorthesis gepubliceerd in de BNAIC 2021 conferentie-proceedings, het artikel is <CustomLink to="https://arxiv.org/abs/2109.04847" external={true}>hier</CustomLink> te lezen. Deze heb ik ook op de conferentie mogen presenteren aan vooraanstaande AI-onderzoekers.
            </>
          ),
        },
        master: {
          title: "MSc. Kunstmatige Intelligentie",
          date: "September 2021 - Juli 2024",
          where: 'Rijksuniversiteit Groningen',
          description: (
            <>
              Cum laude afgestudeerd met een gemiddelde van 8,6 in de Computational Intelligence track. Deze specialisatie richt zich op machine learning; mijn volledige vakkenlijst is <CustomLink to="/documents/master_courses.pdf" download="master_courses.pdf">hier</CustomLink> te vinden.
              Verder heb ik een aangepaste versie van mijn master thesis gepubliceerd in het <CustomLink to="https://pubs.rsc.org/en/journals/journalissues/dd#!recentarticles&adv" external={true}>Digital Discovery journal</CustomLink> (impact factor: 6.2).
              Het artikel is <CustomLink to="https://chemrxiv.org/engage/chemrxiv/article-details/66d20058a4e53c487628f63b" external={true}>hier</CustomLink> te vinden.
            </>
          )
        },
        yag: {
          date: "Mei 2022 - Februari 2024",
          title: 'Strategisch Adviseur',
          description: 
          <div className='flex flex-col gap-y-2'>
          <p>
            Gecoacht door topconsultancybureaus zoals Berenschot, McKinsey, Roland Berger en BCG, heb ik strategisch advies gegeven aan kleine en middelgrote bedrijven, multinationals en overheidsinstanties.
            Voorbeelden van projecten zijn onder andere een levenscyclusanalyse van papieren en digitale boeken en een adviesproject over het gebruik van AI in het Nederlandse onderwijssysteem.
          </p>
          <p>
            Hierbij heb ik veel ervaring opgedaan met het bedenken en initiëren van nieuwe ideeën binnen een grotere organisatie, presenteren en pitchen, leidinggeven aan een team en projectmanagement.
          </p>
          </div>
        },
        omun: {
          title: "Fullstack Website Ontwikkelaar (Oprichter & Directeur)",
          date: "Augustus 2020 - Januari 2024",
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
              Op maat gemaakte web applicaties ontworpen, gebouwd en geïmplementeerd voor klanten, voornamelijk met gebruik van de MERN stack. Een projectvoorbeeld omvatte het ontwikkelen van een platform waarop een grote Maleisische visverkoper al zijn aankopen en verkopen kon registreren.
            </p>
            <p>
              Ik heb geleerd om om te gaan met de uitdagingen die komen kijken bij het implementeren van software en AI voor producten met een grote hoeveelheid gebruikers, waarbij data vaak beperkt is en er meer nodig is dan alleen goede software om tot een resultaat te komen waarmee de klant tevreden is.
            </p>
          </div>
          ),
            },
        kickbox: {
          title: "Kickbokswedstrijd",
          description: `Zes keer per week getraind ter voorbereiding op een kickboksevenement. Hier ging ik uiteindelijk de ring in voor een wedstrijd die ik heb gewonnen. Ik werd getraind door een voormalig wereldkampioen en het trainingskamp duurde in totaal drie maanden.
              
              Dit was een uitdagende periode waarin ik veel discipline en zelfvertrouwen heb ontwikkeld en heb geleerd om door te zetten, ook wanneer ik zowel mentaal als fysiek kapot was.`,
        },
        umcg: {
          date: "Maart 2022 - Maart 2023",
          description: (
          <div className='flex flex-col gap-y-2'>
            <p>
            Gewerkt aan een computer vision project met betrekking tot de detectie van Aspergillus in de longen. We gebruikten machine learning om bewezen schimmelinfecties te onderscheiden van de controlegroep.
            </p>
            <p>
            Mijn belangrijkste leerpunt was dat ik het leuk vind om in een multidisciplinaire omgeving te werken. Ik heb ervaring opgedaan met het begrijpen van de expertise van anderen en deze te vertalen naar het product dat ik voor hen bouwde, en natuurlijk in het eenvoudig uitleggen van mijn eigen expertise aan anderen.
            </p>
          </div>
          ),
          where: 'Universitair Medisch Centrum Groningen',
        },
        bg: {
          date: "September 2020 - April 2024",
          title: "Kapitein en Gids",
          description: (
            <div className='flex flex-col gap-y-2'>
              <p>
              Tours gegeven rond de grachten van Groningen als kapitein van een prachtsloep. 
              
              Dit omvatte ook het geven van historische tours waarbij ik vertelde over de geschiedenis en opmerkelijke delen van de stad.
              </p>
              <p>
              Ik heb veel ervaring opgedaan in het presenteren aan verschillende soorten mensen, geleerd om te gaan met alle soorten persoonlijkheden en om gezag te hebben en te behouden over een groep onbekenden.
              </p>
            </div>
          ),
        },
        doordeweeks: {
          title: "Podcast co-host",
          description: (
            <>
              Een maandelijkse humoristische podcast gerund met cabaretier en goede vriend <CustomLink to="https://maxvanschaik.nl/" external={true}>Max van Schaik</CustomLink>. Dit was een leuk project waarin we uit onze comfortzone gingen om allerlei belachelijke uitdagingen aan te gaan. De afleveringen zijn te vinden op <CustomLink to="https://open.spotify.com/show/6QuNwZVcODW9uakJzpOLs7?si=5c36bacf54af4e77" external={true}>Spotify</CustomLink>. Het leerde mij veel over het opnemen en editen van geluid, en het schrijven en structureren van goede online content.
            </>
          ),
        },
        minor: {
          title: 'Minor Ondernemerschap',
          where: 'Rijksuniversiteit Groningen',
          description: `Naast het volgen van cursussen over wetenschappelijke en bewezen benaderingen van ondernemerschap en de juridische en financiële aspecten daarvan, werkte ik in een multidisciplinair projectteam om een empirisch onderbouwde oplossing te ontwikkelen voor een praktische uitdaging op het gebied van bedrijfsontwikkeling.
          Samen met mijn groepsleden ontwikkelden we een product om te controleren welke privacybeleid van gebruikte producten conflicteren met de privacyvoorkeuren van de gebruiker. We wonnen de prijs voor beste productpitch.`,
        },
        dialogic: {
          date: "Februari 2021 - Juli 2021",
          title: "Machine Learning Engineer (Stagiair)",
          description: `Een applicatie gecreëerd om de gespendeerde tijd aan het labellen van tekstclassificatietaken te verminderen met behulp van het BERT-taalmodel en active learning. De applicatie moest bruikbaar en begrijpelijk zijn voor medewerkers zonder kennis van machine learning.
              
              Ik heb veel geleerd over hoe je een product bouwt voor mensen zonder veel technische ervaring en hoe je een nieuw product integreert in de dagelijkse werkzaamheden van een bedrijf.`,
        },
        jacobianSystems: {
          date: "Augustus 2026 - Heden",
          where: 'Jacobian Systems',
          title: 'Oprichter',
          description: "Ik combineer een uitgebreide technische achtergrond in AI met productervaring om technisch complexe, AI-gedreven producten te bouwen en ontwikkelen voor start- en scale-ups, van nul tot productie, en om bestaande systemen te beoordelen en te bepalen wat beter kan.",
        },
        startupCareer: {
          date: "September 2026 - Heden",
          where: 'STARTUP.CAREER',
          title: 'Mede-oprichter',
          description: "Ik bouw aan een communityplatform voor het Europese startup-ecosysteem die jonge mensen inspireert en helpt om hun carrière op te bouwen bij de meest ambitieuze startups, met professionele film, redactionele artikelen, events en een platform met een groeiend aantal aanmeldingen.",
        },
        onethird: {
          date: "Oktober 2024 - Augustus 2026",
          where: 'OneThird',
          title: 'Data Scientist & ML Engineer',
          description: (
            <div className='flex flex-col gap-y-2'>
              <p>
                OneThird voorspelt de houdbaarheid en kwaliteit van vers fruit en groenten zonder ze te beschadigen. Voor aardbeien, onze grootste categorie, leidde ik de technische roadmap en bouwde ik de data science en machine learning erachter:
              </p>
              <ul className='list-disc list-inside'>
                <li>Automatiseerde houdbaarheids- en Brix-voorspelling op basis van NIR-spectra, metadata en beelden: van handmatig gedraaide algoritmes naar continu hertrainde deep learning-pipelines (eigen 1D-CNN's, transformers) die voor elke klant goed presteren</li>
                <li>Maakte ruisige, subjectieve houdbaarheidslabels objectiever over klanten heen, terwijl ze in lijn bleven met het oordeel van elke klant</li>
                <li>Bouwde het computer vision-systeem van een vroeg prototype uit tot gedetailleerde modellen getraind op 1M+ beelden, die grootte, kleur en defecten (witte schouders, natte plekken, schimmel) beoordelen volgens de specificaties van elke klant; een kwaliteitscheck kost nu ~5 seconden in plaats van minuten</li>
                <li>Verving Power BI door onze eigen analytics-stack voor klantdashboards (Postgres, Cube.js, React)</li>
              </ul>
              <p>
                Na bijna twee jaar stapte ik eruit om voltijds aan mijn eigen dingen te bouwen. Via Jacobian Systems werk ik nog steeds aan projecten met OneThird.
              </p>
            </div>
          ),
        },
        onethirdPm: {
          date: "November 2025 - Augustus 2026",
          where: 'OneThird',
          title: 'Product Manager',
          description: (
            <div className='flex flex-col gap-y-2'>
              <p>
                Naast mijn engineeringwerk was ik eigenaar van ons computer vision-product, rapporterend aan de oprichter:
              </p>
              <ul className='list-disc list-inside'>
                <li>Verantwoordelijk voor de roadmap, go-to-market en het businessmodel, en stuurde de ontwikkelaars aan die het bouwden</li>
                <li>Schaalde het op naar ~20 grote accounts bij top-5 distributeurs in zes Europese landen, en vertaalde klantbehoeften naar productbeslissingen</li>
                <li>Bouwde een herhaalbaar playbook om klanten te onboarden, hun modellen actueel te houden en ze te helpen handmatig testen en verspilling te verminderen</li>
              </ul>
            </div>
          ),
        },
    
        hardlist: {
          date: "September 2025 - Februari 2026",
          title: "Vrijwilliger",
          where: 'Hardlist',
          description: (
            <div className='flex flex-col gap-y-2'>
              <p>
                Ik help bij het op de kaart zetten van Europese hard tech bedrijven voor mensen die er onbekend mee zijn en het verbinden van de al actieve bedrijven.
                Dit doe ik door:
              </p>
              <ul className='list-disc list-inside'>
                <li>Het ondersteunen van de organisatie van evenementen die founders, investeerders, ingenieurs en enthousiastelingen samenbrengen.</li>
                <li>Het onderzoeken van startups voor opname op de <CustomLink to='https://hardli.st' external>Hardli.st</CustomLink> website.</li>
              </ul>
            </div>
          )
        },
        rewind: {
          date: "2025 - Heden",
          title: "Oprichter",
          where: 'Rewind',
          description: (
            <>
              Een zijproject: gedeelde event-fotovaults met een bewuste vertraging — gasten maken de hele avond foto's, maar ze worden pas later ontwikkeld. Gebouwd en onderhouden in mijn vrije tijd, naast <CustomLink to="https://startup.career" external={true}>STARTUP.CAREER</CustomLink>.
            </>
          ),
        },
      },
    },
    footer: {
      copyright: "Pieter Floris Jacobs. Alle rechten voorbehouden.",
      location: "Amsterdam, Nederland"
    },
    notFound: {
      title: "Pagina niet gevonden",
      text: "Deze pagina bestaat niet, of is verplaatst.",
      home: "Terug naar de homepage",
    },
    seo: {
      home: {
        title: "Pieter Floris Jacobs",
        description: "Pieter Floris Jacobs: AI-engineer die met Jacobian Systems bouwt voor start- en scale-ups, met STARTUP.CAREER jong talent naar hen toe brengt en schrijft over het Europese tech-ecosysteem.",
      },
      articles: {
        title: "Artikelen — Pieter Floris Jacobs",
        description: "Artikelen en onderzoek van Pieter Floris Jacobs over kunstmatige intelligentie, van world models en deepfakes tot AI en banen, en het Europese tech-ecosysteem.",
      },
      experience: {
        title: "Ervaring — Pieter Floris Jacobs",
        description: "De carrièretijdlijn van Pieter Floris Jacobs: oprichter van Jacobian Systems en STARTUP.CAREER, product- en AI-engineering bij OneThird, en een MSc in Kunstmatige Intelligentie.",
      },
    },
  }
};

export type Language = 'en' | 'nl';